import { SearchIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Badge,
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Heading,
    Th,
    Thead,
    Tr,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import SortIndicator from 'components/table/SortIndicator';
import { useEffect, useMemo, useState } from 'react';
import { translate } from '../../i18n';
import { SportType } from '../../models/court/dto/court.dto';
import { LeaderboardEntryDto } from '../../models/leaderboard/dto/leaderboard.dto';
import ErrorState from '../../components/states/ErrorState';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../theme/constants';
import { typography } from 'theme/tokens';
import { useNavigateToUserDetails } from '../_shared/calendar/hooks/useNavigateToUserDetails';
import { classifyRead } from '../_shared/readState';
import ScopePrompt from '../_shared/ScopePrompt';
import { useGetCourts } from '../court-management/hooks/useGetCourts';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { useGetLeaderboard } from './hooks/useGetLeaderboard';
import {
    RANK_MEDAL_COLORS,
    getWinRateColor,
    filterByName,
    leaderboardReadView,
} from './utils/leaderboard.utils';

const columnHelper = createColumnHelper<LeaderboardEntryDto>();

export default function LeaderboardPage() {
    const { selectedOrganization } = useSelectedOrganization();
    const { selectedLocation } = useSelectedLocation();
    const navigateToUserDetails = useNavigateToUserDetails();

    const {
        data: courts,
        isInitialLoading: isCourtsInitialLoading,
        isError: isCourtsError,
    } = useGetCourts(selectedLocation?.id);
    const availableSports = useMemo(() => {
        const set = new Set<SportType>();
        (courts ?? []).forEach((c) => (c.sports ?? []).forEach((s) => set.add(s)));
        return Array.from(set).sort();
    }, [courts]);

    const [selectedSport, setSelectedSport] = useState<string>('');
    const activeSport = selectedSport || availableSports[0] || '';

    useEffect(() => {
        if (!selectedSport && availableSports.length > 0) {
            setSelectedSport(availableSports[0]);
        }
    }, [availableSports, selectedSport]);

    // `useGetLeaderboard` is `enabled: Boolean(organizationId) && Boolean(sport)`
    // and React Query v4 reports a DISABLED query as `isLoading: true` with
    // `fetchStatus: 'idle'` — so the raw flag this page used to read drove the
    // "Loading leaderboard..." text FOREVER, and on BOTH of its gate terms.
    // The second one is the interesting half: `sport` comes from the selected
    // LOCATION's courts, so an operator with an organization but no club sat on
    // that sentence indefinitely, with an empty sport picker beside it and
    // nothing on the page able to unstick it. `classifyRead` takes the query's
    // own facts rather than a restatement of its gate (D62), and
    // `leaderboardReadView` owns the three-way answer to "which term is
    // missing" so the page cannot state it two ways.
    const { data, isInitialLoading, isError } = useGetLeaderboard(
        selectedOrganization?.id,
        activeSport,
    );
    const view = leaderboardReadView({
        leaderboard: classifyRead(isError, isInitialLoading, data),
        courts: classifyRead(isCourtsError, isCourtsInitialLoading, courts),
        hasOrganization: Boolean(selectedOrganization),
        hasLocation: Boolean(selectedLocation),
    });

    const [sorting, setSorting] = useState<SortingState>([]);
    const [nameFilter, setNameFilter] = useState('');

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const filteredEntries = useMemo(() => {
        if (!data?.entries) return [];
        return filterByName(data.entries, nameFilter);
    }, [data, nameFilter]);

    const columns = useMemo(() => [
        columnHelper.accessor('rank', {
            id: 'rank',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.rank')}
                </Text>
            ),
            cell: (info) => {
                const rank = info.getValue();
                const medalColor = RANK_MEDAL_COLORS[rank];
                return medalColor ? (
                    <Badge
                        bg={medalColor}
                        color="white"
                        borderRadius="full"
                        px={2}
                        py={1}
                        fontSize="sm"
                        fontWeight="bold"
                    >
                        #{rank}
                    </Badge>
                ) : (
                    <Text color={textColor} fontSize="sm" fontWeight="600">
                        #{rank}
                    </Text>
                );
            },
        }),
        columnHelper.accessor('user', {
            id: 'player',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.player')}
                </Text>
            ),
            cell: (info) => {
                const user = info.getValue();
                return (
                    <Flex align="center" gap={3}>
                        <Avatar
                            size="sm"
                            name={`${user.firstName} ${user.lastName}`}
                            src={user.avatar}
                        />
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                            {user.firstName} {user.lastName}
                        </Text>
                    </Flex>
                );
            },
            sortingFn: (a, b) => {
                const nameA = `${a.original.user.firstName} ${a.original.user.lastName}`;
                const nameB = `${b.original.user.firstName} ${b.original.user.lastName}`;
                return nameA.localeCompare(nameB);
            },
        }),
        columnHelper.accessor('user.level', {
            id: 'level',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.level')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize="sm" fontWeight="600">
                    {info.getValue().toFixed(1)}
                </Text>
            ),
        }),
        columnHelper.accessor('matchesWon', {
            id: 'matchesWon',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.wins')}
                </Text>
            ),
            cell: (info) => (
                <Text color="green.500" fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('matchesLost', {
            id: 'matchesLost',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.losses')}
                </Text>
            ),
            cell: (info) => (
                <Text color="red.500" fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('winRate', {
            id: 'winRate',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.win_rate')}
                </Text>
            ),
            cell: (info) => {
                const rate = info.getValue();
                const pct = (rate * 100).toFixed(0);
                const color = getWinRateColor(rate);
                return (
                    <Badge colorScheme={color} fontSize="xs" px={2} py={1} borderRadius="md">
                        {pct}%
                    </Badge>
                );
            },
        }),
        columnHelper.accessor('rankDelta', {
            id: 'rankDelta',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('leaderboard.trend')}
                </Text>
            ),
            cell: (info) => {
                const delta = info.getValue();
                if (delta == null || delta === 0) {
                    return (
                        <Text color="gray.400" fontSize="sm">
                            —
                        </Text>
                    );
                }
                const isUp = delta > 0;
                return (
                    <Text color={isUp ? 'green.500' : 'red.500'} fontSize="sm" fontWeight="700">
                        {isUp ? '▲' : '▼'} {Math.abs(delta)}
                    </Text>
                );
            },
        }),
    ], [textColor]);

    const table = useReactTable({
        data: filteredEntries,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
                <Flex align="center" gap={4}>
                    <Heading
                        as="h2"
                        color={textColor}
                        {...typography.pageTitle}
                    >
                        {translate('leaderboard.title')}
                    </Heading>
                    {data && (
                        <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
                            {data.total} {translate('leaderboard.players')}
                        </Badge>
                    )}
                </Flex>
            </Flex>

            {/* Filters */}
            <Flex px="25px" mb="16px" gap="4" flexDirection={{ base: 'column', md: 'row' }}>
                <Select
                    maxW={{ base: '100%', md: '250px' }}
                    value={activeSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    borderColor={borderColor}
                >
                    {availableSports.map((sport) => (
                        <option key={sport} value={sport}>
                            {sport}
                        </option>
                    ))}
                </Select>

                <InputGroup maxW={{ base: '100%', md: '300px' }}>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder={translate('leaderboard.search_player')}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        borderColor={borderColor}
                    />
                </InputGroup>
            </Flex>

            <Box>
                {view === 'scope-organization' ? (
                    <ScopePrompt gate="organization" />
                ) : view === 'scope-location' ? (
                    <ScopePrompt gate="location" />
                ) : view === 'loading' ? (
                    <Flex justify="center" py={10}>
                        <Text color="gray.400">{translate('leaderboard.loading')}</Text>
                    </Flex>
                ) : view === 'unreadable' ? (
                    <ErrorState message={translate('leaderboard.error_loading')} />
                ) : filteredEntries.length === 0 ? (
                    <Flex justify="center" py={10}>
                        <Text color="gray.400">{translate('leaderboard.empty')}</Text>
                    </Flex>
                ) : (
                    <Table {...tableProps}>
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th
                                            key={header.id}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor="pointer"
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            <Flex
                                                {...tableThFlexProps}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    ))}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => (
                                <Tr
                                    key={row.id}
                                    {...tableRowProps}
                                    onClick={() =>
                                        navigateToUserDetails?.(row.original.user.id)
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Td
                                            key={cell.id}
                                            {...tableCellProps}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </Box>
        </Card>
    );
}
